import type { Locale } from './index';

const LOCALES = ['en', 'es', 'pt', 'de', 'fr'] as const;

// Paths that MUST NOT carry a locale prefix. Mirrors the middleware exempt
// list so links to verify pages, API endpoints, and static assets stay
// crawlable and shareable across languages.
const EXEMPT_PREFIXES = ['/api', '/verify', '/_next'];

function isExternal(href: string): boolean {
    return /^([a-z]+:)?\/\//i.test(href) || href.startsWith('mailto:') || href.startsWith('tel:');
}

function isFragmentOrQuery(href: string): boolean {
    return href.startsWith('#') || href.startsWith('?');
}

function hasLocalePrefix(path: string): boolean {
    const seg = path.split('/', 2)[1];
    return (LOCALES as readonly string[]).includes(seg);
}

function isExempt(path: string): boolean {
    for (const p of EXEMPT_PREFIXES) if (path === p || path.startsWith(`${p}/`)) return true;
    return false;
}

// Prepend the given locale to an internal absolute path. Returns the input
// unchanged for external URLs, fragments, already-localized paths, and paths
// whose prefix is exempt from locale handling (verify, api, static files).
export function localizedHref(href: string, locale: Locale): string {
    if (!href) return href;
    if (isExternal(href) || isFragmentOrQuery(href)) return href;
    if (!href.startsWith('/')) return href;
    if (hasLocalePrefix(href)) return href;
    if (isExempt(href)) return href;
    if (href === '/') return `/${locale}`;
    return `/${locale}${href}`;
}

// Strip a leading locale prefix if present. Used when swapping languages on
// the current URL — we take the existing pathname, remove its locale, and
// prepend the new one.
export function stripLocalePrefix(pathname: string): string {
    const seg = pathname.split('/', 2)[1];
    if ((LOCALES as readonly string[]).includes(seg)) {
        const rest = pathname.slice(`/${seg}`.length);
        return rest.length === 0 ? '/' : rest;
    }
    return pathname;
}
